import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { ProductListItem, ProductsListResponse, ProductSaveAs } from '@/types';

const STATUS_OPTIONS: { value: ProductSaveAs | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const DISABLED_OPTIONS = [
  { value: '', label: 'Active and disabled' },
  { value: 'false', label: 'Active only' },
  { value: 'true', label: 'Disabled only' },
] as const;

const FEATURED_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Featured only' },
  { value: 'false', label: 'Not featured' },
] as const;

function formatNaira(numStr: string) {
  const n = Number(numStr);
  if (!Number.isFinite(n)) return numStr;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

export function ProductsListPage() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductSaveAs | ''>('');
  const [disabledFilter, setDisabledFilter] =
    useState<(typeof DISABLED_OPTIONS)[number]['value']>('');
  const [featuredFilter, setFeaturedFilter] =
    useState<(typeof FEATURED_OPTIONS)[number]['value']>('');
  const [data, setData] = useState<ProductsListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(disabledFilter && { disabled: disabledFilter }),
      ...(featuredFilter && { featured: featuredFilter }),
    }),
    [page, perPage, search, statusFilter, disabledFilter, featuredFilter]
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ProductsListResponse>('/back-office/products', {
        params,
      });
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load products'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="px-8 py-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Products</h1>
        <p className="text-sm text-slate-500">
          Catalog across all stores. Disable a product to immediately hide it
          from customers.
        </p>
      </div>

      <form
        onSubmit={onSearchSubmit}
        className="mt-6 flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-white p-4"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-600">
            Search
          </label>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Product name"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ProductSaveAs | '');
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Visibility
          </label>
          <select
            value={disabledFilter}
            onChange={(e) => {
              setDisabledFilter(
                e.target.value as (typeof DISABLED_OPTIONS)[number]['value']
              );
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {DISABLED_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Spotlight
          </label>
          <select
            value={featuredFilter}
            onChange={(e) => {
              setFeaturedFilter(
                e.target.value as (typeof FEATURED_OPTIONS)[number]['value']
              );
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {FEATURED_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Apply
        </button>
      </form>

      <div className="mt-6 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && data?.data.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  No products match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              data?.data.map((p) => <ProductRow key={p.id} product={p} />)}
          </tbody>
        </table>
      </div>

      {data && data.pagination.total > 0 && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.total_pages}
          total={data.pagination.total}
          onChange={setPage}
        />
      )}
    </div>
  );
}

function ProductRow({ product }: { product: ProductListItem }) {
  const disabled = !!product.disabled;
  const featured = !!product.is_featured;
  const status = product.save_as ?? 'published';
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link
          to={`/products/${product.id}`}
          className="font-medium text-brand-600 hover:underline"
        >
          {product.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-slate-700">
        {product.store_name ?? <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-slate-700">{formatNaira(product.price)}</td>
      <td className="px-4 py-3 text-slate-700">
        {product.quantity}
        {product.quantity === 0 && (
          <span className="ml-1 text-xs text-red-600">out</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={clsx(
              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
              status === 'published'
                ? 'bg-emerald-100 text-emerald-700'
                : status === 'archived'
                  ? 'bg-slate-200 text-slate-700'
                  : 'bg-amber-100 text-amber-700'
            )}
          >
            {status}
          </span>
          {disabled && (
            <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              disabled
            </span>
          )}
          {featured && (
            <span className="inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
              featured
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
      <div>
        Page {page} of {totalPages} · {total} total
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
